import { DecorationSet } from '@codemirror/view';
import CircularProgress from '@mui/joy/CircularProgress';

import { partition } from 'lodash-es';
import { useEffect, useRef, useState } from 'react';
import {
  useMatches,
  useNavigate,
  useParams,
  useSearchParams,
} from 'react-router-dom';

import Breadcrumbs from '@/components/Breadcrumbs';
import { ErrorBanner } from '@/components/ErrorBanner';
import { cn } from '@/utils/tailwind';

import {
  useExamples,
  useOrganizationId,
  useRunsQuery,
} from '../../hooks/useSwr';
import { RunSchema, RunStatsSchema } from '../../types/schema';
import { getDatasetPath } from '../../utils';
import {
  appExamplePath,
  appPublicDatasetsPath,
  appPublicPath,
  appRunPath,
} from '../../utils/constants';
import { getRootStartDate } from '../../utils/parse-dotted-order-date';
import { RunDetails } from './components/RunDetails';
import { useChildRuns } from './utils/useChildRuns';
import { useGroupChildByParentId } from './utils/useGroupChildByParentId';
import { useRootRunAndRun } from './utils/useRootRunAndRun';
import { CHILD_RUNS_SELECT } from './utils/utils';

function PollOverlay() {
  return (
    <div className="absolute bottom-0 left-0 right-0 top-[60px] flex flex-col items-center justify-center bg-[rgba(255,255,255,0.7)]">
      <CircularProgress />
      <div className="mt-4 text-center text-gray-600">
        <p className="font-bold">Waiting for run...</p>
        <p className="text-sm">This may take a while</p>
      </div>
    </div>
  );
}

export interface RunProps {
  runId?: string | null;
  traceId?: string;
  preloadedRun?: RunSchema;
  preloadedRootRun?: RunSchema;
  traceMaxStartTime?: string;
  traceMinStartTime?: string;
  runSessionId?: string;
  shareTokenProp?: {
    datasetShareToken?: string;
    runShareToken?: string;
  };
  navigateToRun?: (
    id: string,
    trace_id?: string,
    root_start_time?: string
  ) => void;
  comparePosition?: 'left' | 'right';
  scrollRef?: React.RefObject<HTMLDivElement>;
  forceRawRender?: 'raw' | 'rendered';
  getDiffValues?: (type: 'inputs' | 'outputs') =>
    | {
        decoration: DecorationSet;
        generatedCode: string | undefined;
      }
    | undefined;
  showAllRuns?: boolean;
  statsForLatency?: RunStatsSchema;
}
export const Run = (props: RunProps) => {
  const shareToken =
    props.shareTokenProp?.datasetShareToken ||
    props.shareTokenProp?.runShareToken
      ? props.shareTokenProp
      : undefined;
  const params = useParams();
  const runId = props.runId !== undefined ? props.runId : params.runId ?? null;
  const [searchParams] = useSearchParams();
  const doPoll = searchParams.has('poll');
  const traceId = props.traceId ?? searchParams.get('trace_id') ?? undefined;
  const organizationId = useOrganizationId();
  const navigate = useNavigate();
  const matches = useMatches();

  useEffect(() => {
    return () => {
      runFallbackRef.current = {};
    };
  }, []);

  const runFallbackRef = useRef<Record<string, RunSchema>>({});
  const { rootRun: _rootRun, run: _run } = useRootRunAndRun(
    traceId,
    runId,
    shareToken,
    runFallbackRef,
    true,
    props.runSessionId ?? params.sessionId
  );

  let rootRun = _rootRun ?? props.preloadedRootRun;

  const run = _run.data ?? props.preloadedRun;

  // poll until run is available
  useEffect(() => {
    if (doPoll && !_run.data) {
      const interval = setInterval(() => {
        _run.mutate();
      }, 2500);
      return () => clearInterval(interval);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [_run.mutate, _run.data, doPoll]);

  const [searchFilter, setSearchFilter] = useState<string | undefined>(
    undefined
  );

  const isPublicPage = !!(
    shareToken?.runShareToken || shareToken?.datasetShareToken
  );

  const hasFilter = !!(rootRun?.id && !isPublicPage && searchFilter);
  const filterRuns = useRunsQuery(
    hasFilter
      ? { filter: searchFilter, trace: rootRun?.id, select: CHILD_RUNS_SELECT }
      : null,
    undefined,
    { keepPreviousData: false }
  );

  const childRuns = useChildRuns({
    sessionId: props.runSessionId ?? params.sessionId,
    traceId,
    run,
    rootRun,
    isEvaluator: !!params.evaluatorId,
    shareToken,
    traceMaxStartTime: props.traceMaxStartTime,
    traceMinStartTime: props.traceMinStartTime,
  });

  // Partition runs into root and child runs
  const [rootRuns, childRunsOnly] = partition(
    childRuns.data?.runs || [],
    (run) => run.trace_id === run.id
  );

  const _childRunLookup = useGroupChildByParentId(
    childRunsOnly,
    childRuns.isLoading
  );
  // if root run is not loaded from the table,
  // we can use it from the streaming endpoint before the
  // actual root run is loaded.
  const rootRunFromTree = rootRuns[0];
  if (rootRunFromTree) {
    rootRun = rootRunFromTree;
  }

  const childRunLookup =
    childRuns.isStreaming || rootRun ? _childRunLookup : undefined;

  const exampleFetch = useExamples(
    run?.reference_example_id
      ? {
          id: [run.reference_example_id],
          select: [
            'id',
            'created_at',
            'modified_at',
            'name',
            'dataset_id',
            'source_run_id',
            'metadata',
            'inputs',
            'outputs',
            'attachment_urls',
          ],
          as_of: run.start_time,
        }
      : null,
    shareToken,
    { keepPreviousData: false }
  );
  const example = {
    data: exampleFetch.data?.[0],
    isLoading: exampleFetch.isLoading,
  };
  const datasetPath = getDatasetPath(
    example?.data?.dataset_id ?? '',
    organizationId ?? ''
  );
  const examplePath = shareToken?.datasetShareToken
    ? `/${appPublicPath}/${shareToken?.datasetShareToken}/${appPublicDatasetsPath}/${example?.data?.id}/${appExamplePath}`
    : `${datasetPath}/${appExamplePath}/${example?.data?.id}`;

  if (childRuns.error || (_run.error && shareToken)) {
    return (
      <ErrorBanner>
        {childRuns.error?.message || _run.error?.message}
      </ErrorBanner>
    );
  }

  return (
    <>
      {!props.navigateToRun && (
        <div className="-mx-4 mt-3 h-[1px] flex-shrink-0 bg-tertiary" />
      )}
      <RunDetails
        run={run}
        runContents={_run?.contents}
        rootRun={rootRun}
        childRunLookup={childRunLookup}
        searchFilter={hasFilter ? searchFilter : undefined}
        onSearchFilterChange={setSearchFilter}
        isRunLoading={_run.isLoading}
        filteredRuns={filterRuns.data?.runs}
        isFilteredRunsLoading={filterRuns.isLoading}
        isChildRunsLoading={childRuns.isLoading}
        datasetShareToken={shareToken?.datasetShareToken}
        shareToken={shareToken?.runShareToken}
        onNavigate={(run) => {
          // prime the SWR fallback data
          // for immediate feedback of the data
          runFallbackRef.current = { [run.id]: run };
          if (rootRun) runFallbackRef.current[rootRun.id] = rootRun;

          if (props.navigateToRun) {
            props.navigateToRun(
              run.id,
              run.trace_id,
              getRootStartDate(run.dotted_order).toISOString()
            );
          } else {
            navigate(
              `${matches[matches.length - 2].pathname}/${appRunPath}/${
                run.app_path.split('/r/')[1]
              }`
            );
          }
        }}
        example={example.data}
        examplePath={examplePath}
        insidePane={!!props.navigateToRun}
        className={cn(
          props.navigateToRun
            ? '-mx-4 flex-grow overflow-hidden'
            : '-mx-4 -mb-6 min-h-[calc(100vh-57px)]',
          props.comparePosition ? 'mx-0' : ''
        )}
        comparePosition={props.comparePosition}
        scrollRef={props.scrollRef}
        forceRawRender={props.forceRawRender}
        getDiffValues={props.getDiffValues}
        showAllRuns={props.showAllRuns}
        statsForLatency={props.statsForLatency}
      />
      {doPoll && !_run.data && <PollOverlay />}
    </>
  );
};

export default function RunPage() {
  return (
    <div className="flex h-screen flex-col px-4 pb-6 pt-3">
      <Breadcrumbs />
      <Run />
    </div>
  );
}
